import {
    REQUIRED_FILTER_TYPES,
    REQUIRED_FILTER_TO_ENTITY_MAP,
} from './dashboard/constants';

/**
 * Check if a report has any required filters that need configuration
 * This includes entity filters (host, metric, etc.), time_range, host_status, metric_status,
 * host_category, and metric_category
 */
export const hasRequiredFiltersToConfig = (report) => {
    const requiredFilters = report?.requiredFilters || [];

    // Check if report has time_range filter
    const hasTimeRange = requiredFilters.includes('time_range');

    // Check if report has status filters (host_status or metric_status)
    const hasHostStatusFilter = requiredFilters.includes('host_status');
    const hasMetricStatusFilter = requiredFilters.includes('metric_status');

    // Check if report has category filters (host_category or metric_category)
    const hasHostCategoryFilter = requiredFilters.includes('host_category');
    const hasMetricCategoryFilter = requiredFilters.includes('metric_category');

    // Check if report has entity filters
    const hasEntityFilters = REQUIRED_FILTER_TYPES.some(
        (key) =>
            requiredFilters.includes(key) && REQUIRED_FILTER_TO_ENTITY_MAP[key],
    );

    return hasTimeRange || hasHostStatusFilter || hasMetricStatusFilter || hasHostCategoryFilter || hasMetricCategoryFilter || hasEntityFilters;
};

/**
 * Build URL query string from filters object
 * Useful for generating shareable report links
 *
 * @param {Object} filters - Filters object
 * @returns {string} URL query string (without leading "?")
 *
 * @example
 * buildQueryParamsFromFilters({ entities: { business_services: [5] }, timeRange: { mode: 'predefined', range: 'last_7d' } })
 * // Returns: "business_services=5&time_range=last_7d"
 */
export const buildQueryParamsFromFilters = (filters) => {
    if (!filters) return '';

    const params = new URLSearchParams();

    // Add entity filters
    if (filters.entities) {
        if (filters.entities.hosts?.length > 0) {
            params.set('hosts', filters.entities.hosts.join(','));
        }
        if (filters.entities.metrics?.length > 0) {
            params.set('metrics', filters.entities.metrics.join(','));
        }
        if (filters.entities.business_services?.length > 0) {
            params.set('business_services', filters.entities.business_services.join(','));
        }
        if (filters.entities.host_groups?.length > 0) {
            params.set('host_groups', filters.entities.host_groups.join(','));
        }
    }

    // Add category filters (outside of entities, like hostStatuses/metricStatuses)
    if (filters.hostCategories?.length > 0) {
        params.set('host_categories', filters.hostCategories.join(','));
    }
    if (filters.metricCategories?.length > 0) {
        params.set('metric_categories', filters.metricCategories.join(','));
    }

    // Add time range
    if (filters.timeRange) {
        if (filters.timeRange.mode === 'predefined' && filters.timeRange.range) {
            params.set('time_range', filters.timeRange.range);
        } else if (filters.timeRange.mode === 'custom') {
            if (filters.timeRange.start) {
                params.set('time_from', filters.timeRange.start);
            }
            if (filters.timeRange.end) {
                params.set('time_to', filters.timeRange.end);
            }
        }
    }

    // Add status filters
    if (filters.hostStatuses?.length > 0) {
        params.set('host_statuses', filters.hostStatuses.join(','));
    }
    if (filters.metricStatuses?.length > 0) {
        params.set('metric_statuses', filters.metricStatuses.join(','));
    }

    return params.toString();
};
